import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

export default function PrivacyPolicyPage() {
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'Política de Privacidad | Servicio a Tiempo';
  }, []);

  const Section = ({ id, title, children }: { id?: string; title: string; children: React.ReactNode }) => (
    <section id={id} className="max-w-3xl mx-auto bg-white rounded-2xl shadow-lg border border-gray-100 p-6 mb-6">
      <h2 className="text-xl font-bold text-[#2C4358] mb-3">{title}</h2>
      <div className="text-[#4b5563] leading-relaxed text-sm space-y-3">
        {children}
      </div>
    </section>
  );

  const today = new Date().toISOString().substring(0, 10);

  return (
    <div className="min-h-screen bg-[#e9ecef]">
      {/* Header */}
      <header className="bg-gradient-to-r from-[#22B6B2] to-[#2C4358] text-white">
        <div className="max-w-3xl mx-auto px-4 py-8">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate(-1)}
              className="rounded-lg px-3 py-2 bg-white/10 hover:bg-white/20 transition-colors"
              aria-label="Regresar"
              title="Regresar"
            >
              ←
            </button>
            <img
              src="/assets/ots/Servicio_a_Tiempo_logo_256x256.png"
              alt="Servicio a Tiempo"
              className="w-10 h-10 object-contain fade-edges"
            />
          </div>
          <h1 className="text-3xl font-bold mt-6">Política de Privacidad</h1>
          <p className="text-white/80 mt-2 text-sm">
            Última actualización: {today}
          </p>
        </div>
      </header>

      {/* Content */}
      <main className="px-4 py-8">
        <section className="max-w-3xl mx-auto mb-6">
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
            <p className="text-[#4b5563] text-sm leading-relaxed">
              Esta Política de Privacidad explica cómo “Servicio a Tiempo” (el “Proveedor”) trata los datos
              personales de los usuarios de la plataforma (la “Plataforma”), incluyendo restaurantes, su equipo y
              comensales. Al utilizar la Plataforma, usted acepta esta Política.
            </p>
          </div>
        </section>

        <Section title="1. Alcance y Responsable">
          <p>El responsable del tratamiento es el Proveedor que opera la Plataforma “Servicio a Tiempo”. Esta Política aplica a los datos tratados a través de la Plataforma y canales relacionados de soporte.</p>
        </Section>

        <Section title="2. Categorías de Datos Tratados">
          <ul className="list-disc pl-5 space-y-1">
            <li><b>Datos de cuenta</b>: nombre, correo electrónico, rol y metadatos de perfil.</li>
            <li><b>Datos operativos</b>: menús, categorías, productos, precios, promociones, mesas, pedidos y estados.</li>
            <li><b>Datos técnicos</b>: dirección IP, tipo de dispositivo, identificadores técnicos, logs de uso y eventos.</li>
            <li><b>Tokens de notificación</b>: por ejemplo, FCM para enviar notificaciones push.</li>
            <li><b>Soporte</b>: comunicaciones con el equipo de soporte y metadatos de incidencias.</li>
          </ul>
        </Section>

        <Section title="3. Contenido Público y Divulgación Controlada">
          <p>La Plataforma está diseñada para que los Restaurantes publiquen información comercial destinada al público, como menús, precios, fotos de productos y promociones. Al cargar dichos contenidos, el Restaurante declara y garantiza que:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Es titular o tiene autorización para publicar ese contenido.</li>
            <li>Comprende que la información marcada o configurada como visible al público será accesible por comensales y visitantes según la funcionalidad de la Plataforma.</li>
          </ul>
          <p>Los datos personales no destinados a publicación (por ejemplo, correos de miembros del equipo) no se hacen públicos y se mantienen conforme a esta Política.</p>
        </Section>

        <Section title="4. Finalidades del Tratamiento">
          <ul className="list-disc pl-5 space-y-1">
            <li>Proveer y mantener la operación de la Plataforma (autenticación, permisos por rol, gestión de menús, pedidos, mesas y notificaciones).</li>
            <li>Mejorar la seguridad, prevenir fraude y garantizar la integridad del servicio.</li>
            <li>Atender soporte y comunicaciones operativas.</li>
            <li>Analítica y métricas de uso agregadas para mejorar funcionalidades y rendimiento.</li>
          </ul>
        </Section>

        <Section title="5. Base Jurídica">
          <ul className="list-disc pl-5 space-y-1">
            <li>Ejecución del contrato/relación con Restaurantes y sus equipos.</li>
            <li>Interés legítimo en seguridad, prevención de fraude y mejora del servicio.</li>
            <li>Consentimiento, cuando resulte aplicable (por ejemplo, notificaciones push en ciertos dispositivos).</li>
            <li>Cumplimiento de obligaciones legales aplicables.</li>
          </ul>
        </Section>

        <Section title="6. Pagos con Proveedor Externo (No almacenamos tarjetas)">
          <p>Cuando se habiliten pagos o registro de pagos, la Plataforma puede integrarse con proveedores externos de procesamiento de pagos. <b>No almacenamos ni procesamos datos de tarjetas</b> en nuestros servidores.</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Los datos de tarjeta y credenciales de pago se capturan y procesan directamente por el proveedor externo conforme a sus propios términos y políticas.</li>
            <li>Podemos recibir identificadores de transacción, estados de pago y metadatos necesarios para conciliar o habilitar funciones dentro de la Plataforma.</li>
          </ul>
        </Section>

        <Section title="7. Notificaciones Push y Tokens">
          <p>Para enviar notificaciones (por ejemplo, cambios de estado de pedidos), podemos almacenar tokens de notificación (como FCM). Usted puede desactivar las notificaciones desde la interfaz o los ajustes del dispositivo.</p>
        </Section>

        <Section title="8. Destinatarios y Encargados">
          <p>Podemos compartir datos con proveedores que actúan como encargados de tratamiento para alojamiento, mensajería push, analítica, monitoreo y soporte. Dichos proveedores solo tratarán datos según nuestras instrucciones y con medidas de seguridad adecuadas.</p>
        </Section>

        <Section title="9. Transferencias Internacionales">
          <p>Si se realizan transferencias fuera de su país, se aplicarán salvaguardas adecuadas (por ejemplo, cláusulas contractuales estándar u otros mecanismos admitidos por la normativa aplicable).</p>
        </Section>

        <Section title="10. Conservación de Datos">
          <p>Conservamos los datos mientras sean necesarios para brindar los servicios, cumplir obligaciones legales, resolver disputas y hacer cumplir acuerdos. Los periodos pueden variar según la categoría de datos y las necesidades operativas o legales.</p>
        </Section>

        <Section title="11. Seguridad">
          <p>Aplicamos medidas técnicas y organizativas razonables para proteger los datos frente a acceso no autorizado, alteración o destrucción. No obstante, ningún sistema es 100% seguro; usted también debe proteger sus credenciales y dispositivos.</p>
        </Section>

        <Section title="12. Derechos de los Titulares">
          <p>Dependiendo de la normativa aplicable, usted puede ejercer derechos de acceso, rectificación, supresión, oposición, limitación, portabilidad y revocación del consentimiento. Para ejercerlos, contacte al soporte indicado más abajo.</p>
        </Section>

        <Section title="13. Cookies y Tecnologías Similares">
          <p>La Plataforma puede usar cookies y tecnologías similares para autenticación, seguridad, analítica y mejora de experiencia. Puede gestionar cookies desde su navegador. Ciertas funciones pueden verse afectadas si las deshabilita.</p>
        </Section>

        <Section title="14. Menores de Edad">
          <p>La Plataforma no está dirigida a menores sin consentimiento válido de sus representantes legales, cuando sea requerido. Si detectamos datos de menores sin base válida, podremos eliminarlos.</p>
        </Section>

        <Section title="15. Cambios a esta Política">
          <p>Podemos actualizar esta Política para reflejar cambios en la Plataforma o normativa. Publicaremos la versión vigente con su fecha. El uso continuado implica aceptación de los cambios.</p>
        </Section>

        <Section title="16. Contacto">
          <p>Para consultas, ejercicio de derechos o reportar incidentes, utilice los canales de soporte indicados en la Plataforma. Incluya información suficiente para identificar su cuenta y entender su solicitud.</p>
        </Section>
      </main>
    </div>
  );
}


